import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningIcon from '@mui/icons-material/Warning';
import { Breadcrumbs, Stack, Tooltip, Typography } from '@mui/material';
import { getLinkedinSearchProjectById } from 'API/linkedinsearchproject';
import { AnalysisStatus } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import { LinkedinSearchProject, SuspensionReasons } from './linkedin-profile-finder.types';

interface Props {
  project: LinkedinSearchProject;
  isEven: boolean;
  startSearchLoading: boolean;

  startSearch(project: LinkedinSearchProject): () => void;

  restartSearch(project: LinkedinSearchProject): () => void;

  downloadSearchResults(id: string): () => void;
}

const SearchProjectItem: React.FC<Props> = ({ project, isEven, startSearchLoading, startSearch, restartSearch, downloadSearchResults }) => {
  const { classes } = useStyles({ isEven });
  const { t } = useTranslation();
  useUserData();

  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>(AnalysisStatus.NOT_CONNECTED);
  const [analyzedContacts, setAnalyzedContacts] = useState<number>();

  let projectInterval: any;

  const fetchSingleProject = async () => {
    const res = await getLinkedinSearchProjectById(project.id);

    if (res && !res.error) {
      const resProject: LinkedinSearchProject = res;

      setAnalyzedContacts(resProject.searchKeywords.filter((contact) => contact.isFinished).length);

      if (!_.isNil(projectInterval) && resProject.analysisFinished) {
        clearInterval(projectInterval);
        setAnalysisStatus(AnalysisStatus.DONE);
      }
    }
  };

  useEffect(() => {
    if (!project.status) {
      setAnalysisStatus(AnalysisStatus.EXPIRED);
    } else if (!project.analysisStarted) {
      setAnalysisStatus(AnalysisStatus.NOT_CONNECTED);
    } else if (project.isSuspended) {
      setAnalysisStatus(AnalysisStatus.SUSPENDED);
    } else if (project.analysisStarted && !project.analysisFinished) {
      setAnalysisStatus(AnalysisStatus.PENDING);
    } else if (project.analysisFinished) {
      setAnalysisStatus(AnalysisStatus.DONE);
    }

    if (project.analysisStarted && !project.analysisFinished) {
      projectInterval = setInterval(() => {
        fetchSingleProject();
      }, 1 * 60 * 1000);
    }

    return () => {
      clearInterval(projectInterval);
    };
  }, [project.analysisStarted, project.analysisFinished, project.isSuspended, project.status]);

  const countAnalyzedContacts = () => {
    return project.searchKeywords.filter((keyword) => keyword.isFinished).length;
  };

  const generateProgressCount = () => {
    const currentAnalyzed = _.isNil(analyzedContacts) ? countAnalyzedContacts() : analyzedContacts;
    const totalContacts = project.searchKeywords.length;

    return (
      <>
        <PaiDotLoader size={'small'} inline={true} />
        <Typography className={classes.statusText} marginLeft={'16px'}>
          {t('progressCount', { current: currentAnalyzed, total: totalContacts })}
        </Typography>
      </>
    );
  };

  const generateSuspensionReason = () => {
    switch (project.suspensionReason) {
      case SuspensionReasons.COOKIES_INVALID:
        return `${t('reason')}: ${t('cookieInvalidTitle')}`;

      default:
        if (!_.isNil(project.suspensionReason)) {
          return `${t('reason')}: ${project.suspensionReason}`;
        }
        return `${t('reason')}: ${t('unknownError')}`;
    }
  };

  return (
    <>
      <div className={classes.crawlerProject}>
        <Stack direction={'row'} gap={1} justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'}>
          <Typography className={classes.projectName}>{`${project.name} - ${project.searchKeywords.length} ${
            project.searchKeywords.length > 1 ? t('profiles') : t('profile')
          }`}</Typography>
          <Breadcrumbs className={classes.breadcrumbs}>
            {analysisStatus === AnalysisStatus.DONE ? (
              <div className={classes.flexContainer}>
                <FileDownloadIcon className={classes.icon} />
                <PaiLink
                  text={t('export')}
                  size={'sm'}
                  variant={'primary'}
                  underline={'always'}
                  textTransform={'lowercase'}
                  action={downloadSearchResults(project.id)}
                />
              </div>
            ) : null}
            {analysisStatus === AnalysisStatus.SUSPENDED ? (
              <div className={classes.flexContainer}>
                <RestartAltIcon className={classes.icon} />
                <PaiLink
                  text={t('restart')}
                  size={'sm'}
                  variant={'primary'}
                  underline={'always'}
                  textTransform={'lowercase'}
                  action={restartSearch(project)}
                />
              </div>
            ) : null}
            <div className={classes.flexContainer}>
              {analysisStatus === AnalysisStatus.DONE ? (
                <>
                  <CheckCircleIcon className={classes.icon} />
                  <Typography className={classes.statusText}>{t('completed')}</Typography>
                </>
              ) : analysisStatus === AnalysisStatus.NOT_CONNECTED ? (
                startSearchLoading ? (
                  <PaiDotLoader size={'small'} inline={true} />
                ) : (
                  <PaiLink
                    text={t('startNow')}
                    size={'sm'}
                    variant={'primary'}
                    underline={'always'}
                    textTransform={'lowercase'}
                    disabled={startSearchLoading}
                    action={startSearch(project)}
                  />
                )
              ) : analysisStatus === AnalysisStatus.SUSPENDED ? (
                <>
                  <Tooltip title={generateSuspensionReason()}>
                    <WarningIcon className={classes.warnIcon} />
                  </Tooltip>
                  <Typography className={classes.statusText}>{t('suspended')}</Typography>
                </>
              ) : null}
            </div>
          </Breadcrumbs>
          {analysisStatus === AnalysisStatus.PENDING ? <div className={classes.flexContainer}>{generateProgressCount()}</div> : null}
        </Stack>
      </div>
    </>
  );
};

const useStyles = makeStyles<{ isEven: boolean }>()((theme, { isEven }) => ({
  crawlerProject: {
    position: 'relative',
    width: '100%',
    padding: '8px',
    background: isEven ? '#FFF' : theme.palette.backgrounds.main,
    minHeight: '36px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    marginRight: '8px',
    color: theme.palette.greys.medium,
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(16),
      marginRight: '4px',
    },
  },
  projectName: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    lineHeight: 1,
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
  statusText: {
    fontSize: theme.typography.pxToRem(12),
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
  },
  breadcrumbs: {
    '& .MuiBreadcrumbs-separator': {
      color: theme.palette.greys.medium,
      height: theme.typography.pxToRem(20),
      alignItems: 'center',
      [theme.breakpoints.down(400)]: {
        height: theme.typography.pxToRem(16),
      },
    },
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  warnIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.error.main,
    marginRight: '8px',
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(16),
      marginRight: '4px',
    },
  },
}));

export default SearchProjectItem;
